import type { FragmentData } from "@gqlb/core";
import { Check } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { CurrencyRenderer } from "@/components/CurrencyRenderer";
import type { SelectionState } from "@/graphql/fragments";
import { OPTION_SELECTOR_FRAGMENT } from "@/graphql/fragments";
import { SelectionVerbiageNew } from "./SelectionVerbiageNew";
import { SubOptionSelectorNew } from "./SubOptionSelectorNew";

export { OPTION_SELECTOR_FRAGMENT };

export const OptionSelectorNew: React.FC<{
  onSelectionUpdate: (
    selectionUpdateFn: (selectionState: SelectionState[string]) => SelectionState[string],
  ) => void;
  selections: SelectionState[string];
  data: FragmentData<typeof OPTION_SELECTOR_FRAGMENT>;
  productId: string;
  showTitle: boolean;
}> = ({ data, selections, onSelectionUpdate, productId, showTitle }) => {
  const [openSubOption, setOpenSubOption] = useState<string | null>(null);

  const isSingle = data.minSelections === 1 && data.maxSelections === 1;
  const currentCount = selections.selections.size + Object.keys(selections.subOptions).length;

  return (
    <div data-option-id={data.id}>
      {/* Header */}
      {showTitle && (
        <div className="mb-2">
          <h3 className="text-sm font-semibold text-gray-700">
            {data.name}
            <CurrencyRenderer freeStyle="null" prefix=" — " data={data.price} />
          </h3>
          <SelectionVerbiageNew
            minSelection={data.minSelections}
            maxSelection={data.maxSelections}
            currentCount={currentCount}
          />
        </div>
      )}

      {/* Grid of options */}
      {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-label is conditionally applied with radiogroup role */}
      <div
        role={isSingle ? "radiogroup" : undefined}
        aria-label={isSingle ? data.name : undefined}
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-2"
      >
        {data.selections.map((selection) => {
          if (selection.__typename === "EmbeddedProductOption") {
            return (
              <div key={selection.id} className="col-span-full">
                <SubOptionSelectorNew
                  isOpen={openSubOption === selection.id}
                  setIsOpen={(open) => {
                    setOpenSubOption((prev) => {
                      if (open) return selection.id;
                      if (prev !== selection.id) return selection.id;
                      return null;
                    });
                  }}
                  productId={productId}
                  optionId={selection.id}
                  data={selection}
                  selections={selections}
                  onSelectionUpdate={(stateUpdateFn) => {
                    onSelectionUpdate((prev) => {
                      const newState = stateUpdateFn(prev);
                      const selectionCount =
                        newState.selections.size + Object.keys(newState.subOptions).length;
                      if (data.maxSelections !== null && selectionCount > data.maxSelections) {
                        let elem = newState.order.shift();
                        while (elem !== undefined) {
                          if (!newState.selections.delete(elem)) {
                            delete newState.subOptions[elem];
                          }
                          const count =
                            newState.selections.size + Object.keys(newState.subOptions).length;
                          if (count <= data.maxSelections) break;
                          if (newState.order.length === 0) break;
                          elem = newState.order.shift();
                        }
                      }
                      return newState;
                    });
                  }}
                />
              </div>
            );
          }

          const isSelected = selections.selections.has(selection.id);

          const onClick = () => {
            onSelectionUpdate((prev) => {
              const sels = new Set(prev.selections);
              const subOptions = { ...prev.subOptions };
              const order = [...prev.order];
              if (sels.has(selection.id)) {
                sels.delete(selection.id);
                order.splice(order.indexOf(selection.id), 1);
              } else {
                const count = sels.size + Object.keys(subOptions).length;
                if (data.maxSelections !== null && count >= data.maxSelections) {
                  let elem = order.shift();
                  while (elem !== undefined) {
                    if (!sels.delete(elem)) {
                      delete subOptions[elem];
                    }
                    const c = sels.size + Object.keys(subOptions).length;
                    if (c < data.maxSelections) break;
                    if (order.length === 0) break;
                    elem = order.shift();
                  }
                }
                sels.add(selection.id);
                order.push(selection.id);
              }
              return { selections: sels, subOptions, order };
            });
          };

          return (
            // biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-checked is conditionally applied with radio role
            <button
              key={selection.id}
              type="button"
              role={isSingle ? "radio" : undefined}
              aria-checked={isSingle ? isSelected : undefined}
              aria-pressed={!isSingle ? isSelected : undefined}
              onClick={onClick}
              className={`
                flex items-center gap-2.5 px-3 py-2.5 rounded-xl border
                text-left select-none cursor-pointer
                transition-all duration-200
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-mint/50 focus-visible:ring-offset-1
                ${
                  isSelected
                    ? "border-brand-mint/50 bg-brand-mint/10"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }
              `}
            >
              {/* Selection indicator */}
              <span
                className={`
                  flex items-center justify-center size-5 border-2 shrink-0
                  transition-all duration-150 ease-in-out
                  ${isSingle ? "rounded-full" : "rounded-[4px]"}
                  ${isSelected ? "border-brand-mint bg-brand-mint" : "border-gray-400 bg-white"}
                `}
              >
                <span
                  className={`transition-transform duration-150 ${
                    isSelected ? "scale-100" : "scale-0"
                  }`}
                >
                  {isSingle ? (
                    <span className="block size-2 rounded-full bg-brand-black" />
                  ) : (
                    <Check className="size-3 text-brand-black" strokeWidth={3} />
                  )}
                </span>
              </span>

              {/* Name */}
              <span
                className={`flex-1 text-sm text-brand-black ${isSelected ? "font-semibold" : ""}`}
              >
                {selection.name}
              </span>

              {/* Price */}
              <span className="text-sm text-gray-500 shrink-0 tabular-nums">
                <CurrencyRenderer data={selection.price} freeStyle="null" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
